/*
 * File: DataStore.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: MST store for Scheduling
 */
import {
  types,
  flow,
  applySnapshot,
  destroy,
  cast,
  Instance,
  SnapshotIn
} from "mobx-state-tree";
import ApptModel from "./ApptModel";
import Api from "../Api";
import PatientIdStorage from "../../patients/store/PatientIdStore";
import { parseISO } from "date-fns";
import { StoreManager } from "utils/useStore";

const DataStore = types
  .model("ApptStore", {
    /** The loading state for the store. `null` when no loading is in progress, a number when loading a specific appointment, and the string `"list"` when loading multiple appointments based on filters. */
    loadingId: types.maybeNull(
      types.union(types.literal("list"), types.number)
    ),
    dataLoaded: false,
    // Not clear why all of these models for wildly different parts of the app are all in this store together.
    // Leaving it for now since separating them out would be difficult to do without breaking something at the moment.
    appts: types.optional(types.array(ApptModel), []),
    selectedAppt: types.maybe(ApptModel),
    newAppt: types.optional(ApptModel, {}),
    editAppt: types.optional(ApptModel, {})
  })
  .volatile(() => ({
    transparencies: []
  }))

  .views(self => ({
    /**
     * The currently selected appointment's ID. When currently loading an appointment, the ID of the loading appointment is used, otherwise the ID of selectedAppt is used. If there is no selected appointment, `null` is returned.
     * @returns
     */
    selectedAppointmentId() {
      if (self.loadingId === null || self.loadingId === "list") {
        return self.selectedAppt?.appointmentId ?? null;
      } else {
        return self.loadingId;
      }
    },

    /**
     * A boolean value indicating whether a loading operation is currently in progress.
     * This is a computed property for backwards compatibility with the old `loading` boolean flag which was changed to `loadingId`
     */
    get loading() {
      return self.loadingId != null;
    }
  }))

  .actions(self => ({
    fetchAppts: flow(function*(storeManager) {
      const filtersPref = storeManager.getPreference("apptFilters");
      if (self.dataLoaded) return;
      self.loadingId = "list";
      self.appts = cast([]);
      const filters = filtersPref.getUrl(storeManager);
      const transparenciesFilters = yield filtersPref.getTransparenciesUrl(
        storeManager
      );
      try {
        const appts = yield Api.fetch(filters);
        self.transparencies = yield Api.fetchTransparencies(
          transparenciesFilters
        );
        self.appts = appts;
        self.dataLoaded = true;
        self.loadingId = null;
      } catch (error) {
        self.loadingId = null;
        console.log("Error fetching appts...", error);
      }
    })
  }))
  .actions(self => ({
    reloadAppts(storeManager: StoreManager) {
      self.dataLoaded = false;
      self.fetchAppts(storeManager);
    },

    fetchAppointment: flow(function*(apptId) {
      try {
        const parsedApptId = parseInt(apptId);
        self.loadingId = parsedApptId;
        if (self.selectedAppt) destroy(self.selectedAppt);
        if (self.editAppt) destroy(self.editAppt);
        if (apptId === "undefined" || apptId === null || isNaN(parsedApptId)) {
          self.loadingId = null;
          return;
        }

        self.selectedAppt = undefined;
        self.editAppt = cast({});
        const appt = yield Api.find(apptId);
        if (appt.startTime) {
          appt.startTime = parseISO(appt.startTime);
          appt.start = appt.startTime;
          appt.appointmentDate = appt.startTime;
          appt.appointmentTime = appt.startTime;
        }
        PatientIdStorage.setId(appt.patientId);
        self.selectedAppt = appt;
        self.editAppt = appt;
        self.loadingId = null;
      } catch (error) {
        self.loadingId = null;
        console.log(
          "Error fetching appointment with id " +
            apptId +
            " from apointment store",
          error
        );
      }
    }),

    addAppointment(appt: Instance<typeof ApptModel>) {
      self.appts.push(appt);
      self.newAppt = cast({});
    },

    updateAppointment(appt: SnapshotIn<typeof ApptModel>) {
      const appointment = self.appts.find(
        a => appt.appointmentId === a.appointmentId
      );
      if (appointment) {
        applySnapshot(appointment, appt);
      }
      if (self.selectedAppt) {
        applySnapshot(self.selectedAppt, appt);
      }
      applySnapshot(self.editAppt, appt);
    }
  }));

export default DataStore;
